// App entry
import { useEffect } from 'react';
import './App.css';
import './styles/Common.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import SelectExercise from "./pages/SelectExercise";
import LogExercise from "./pages/LogExercise";
import CreateExercise from "./pages/CreateExercise";
import ExerciseDetails from "./pages/ExerciseDetails";
import SiteFooter from "./components/SiteFooter";
import AuthSignin from "./pages/AuthSignin";
import AuthSignup from "./pages/AuthSignup";
import SsoCallback from "./pages/SsoCallback";
import { isClerkEnabled } from "./utils/clerkConfig";
import { I18nProvider, useI18n, getLanguageFromPath } from "./i18n/i18n";
import { PrerenderSnapshotMarker, useSnapshotRenderMode } from "./utils/prerender";
import AnalyticsPageTracker from "./components/AnalyticsPageTracker";
import AnalyticsAuthTracker from "./components/AnalyticsAuthTracker";

import { getExercisesList } from "./services/apiClient";

function LanguageSync() {
  const location = useLocation();
  const { language, setLanguage } = useI18n();

  useEffect(() => {
    const target = getLanguageFromPath(location.pathname);
    if (language !== target) {
      setLanguage(target);
    }
  }, [location.pathname, language, setLanguage]);

  useEffect(() => {
    getExercisesList(language)
      .then(exercises => {
        if (Array.isArray(exercises) && exercises.length > 0) {
          localStorage.setItem('cached_exercises', JSON.stringify(exercises));
        }
      })
      .catch(err => {
        console.warn("Failed to fetch exercise catalog from API:", err.message);
      });
  }, [language]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const snapshotMode = useSnapshotRenderMode();

  return (
      <Router>
        <I18nProvider>
          {snapshotMode && <PrerenderSnapshotMarker />}
          <ScrollToTop />
          <LanguageSync />
          <AnalyticsPageTracker />
          <AnalyticsAuthTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/SelectExercise" element={<SelectExercise />} />
            <Route path="/SelectExercise/:muscleGroup" element={<SelectExercise />} />
            <Route path="/CreateExercise" element={<CreateExercise />} />
            <Route path="/LogExercise/:exerciseId" element={<LogExercise />} />
            <Route path="/LogExercise/:exerciseId/:date" element={<LogExercise />} />
            <Route path="/exercises/:exerciseId" element={<ExerciseDetails />} />
            <Route path="/uk" element={<Home />} />
            <Route path="/uk/SelectExercise" element={<SelectExercise />} />
            <Route path="/uk/SelectExercise/:muscleGroup" element={<SelectExercise />} />
            <Route path="/uk/CreateExercise" element={<CreateExercise />} />
            <Route path="/uk/LogExercise/:exerciseId" element={<LogExercise />} />
            <Route path="/uk/LogExercise/:exerciseId/:date" element={<LogExercise />} />
            <Route path="/uk/exercises/:exerciseId" element={<ExerciseDetails />} />
            {isClerkEnabled && (
              <>
                <Route path="/sign-in" element={<AuthSignin />} />
                <Route path="/sign-up" element={<AuthSignup />} />
                <Route path="/uk/sign-in" element={<AuthSignin />} />
                <Route path="/uk/sign-up" element={<AuthSignup />} />
                {/* OAuth/SAML SSO callback routes for Clerk */}
                <Route path="/sign-in/sso-callback" element={<SsoCallback />} />
                <Route path="/sign-up/sso-callback" element={<SsoCallback />} />
                <Route path="/uk/sign-in/sso-callback" element={<SsoCallback />} />
                <Route path="/uk/sign-up/sso-callback" element={<SsoCallback />} />
                {/* Wildcard routes to support Clerk's multi-step flows like /verify-email-address, /factor-one, etc. */}
                <Route path="/sign-in/*" element={<AuthSignin />} />
                <Route path="/sign-up/*" element={<AuthSignup />} />
                <Route path="/uk/sign-in/*" element={<AuthSignin />} />
                <Route path="/uk/sign-up/*" element={<AuthSignup />} />
              </>
            )}
          </Routes>
          <SiteFooter />
        </I18nProvider>
      </Router>
  );
}

export default App;
